import { autoinject, transient } from "aurelia-dependency-injection";
import { type WorkflowConfigurationModel, WorkflowActivityModel, WorkflowActivitySignature, WorkflowActivityPathResult, CloneWorkflowConfigurationRequest } from "../../../../models/workflow/workflow.models";
import { WorkflowConfigurationService } from "../../../../services/workflow/workflow-configuration-service";
import { computedFrom, Disposable } from "aurelia-framework";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { BindingEngine } from "aurelia-framework";
import { Guid } from "../../../../utilities/guid";
import { ArrayUtils } from "../../../../utilities/arrays";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import * as Enumerable from "linq";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { NetworkStructure, NetworkNode, NetworkLink } from "../../../components/network/network-structure";
import { Compose } from "aurelia-templating-resources";
import { BaseActivityConfiguration } from "../../../plugins/workflow-activities/base-activity-configuration";
import { AllEntityTypeConfigurationConstants, EntityTypeModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { I18N } from "aurelia-i18n";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../../constants/authentication/base-claims";
import { BrowserHelper } from "../../../../helpers/browser-helper";
import { Reflection } from "../../../../utilities/reflection";
const vis = !BrowserHelper.isIE()
    ? await import('vis/dist/vis-network.min.js')
    : null;

@autoinject()
@transient()
export class WorkflowConfigurationViewModel {

    private _params: { id: number, entityTypeId: number };
    private _branchId: number;
    private _entityType: number;
    private _nodes: any;
    private _edges: any;
    private _selectedItem: WorkflowConfigurationModel;
    private _network: any;
    private _networkStructure: NetworkStructure = new NetworkStructure();
    private _selectedNode: NetworkNode;
    private _originalSelectedItem: WorkflowConfigurationModel;
    private _bindingSubscriptions: Disposable[] = [];

    public canCreateWorkflowConfiguration: boolean = false;
    public uow: UnitOfWorkObserver<WorkflowConfigurationModel>;
    public workflowConfigurations: WorkflowConfigurationModel[] = [];
    public workflowActivitySignatures: WorkflowActivitySignature[] = [];
    public entityTypes: EntityTypeModel[] = [];
    public divNetwork: HTMLDivElement;
    public activityConfigComposer: Compose;
    public pathResults: WorkflowActivityPathResult[] = [];
    public networkCanvas: HTMLCanvasElement;

    public set selectedNode(value: NetworkNode) {
        this._selectedNode = value;
        setTimeout(() => {
            if (this._network) {
                this._network.setSize(this.divNetwork.offsetWidth, this.divNetwork.offsetHeight);
            }
        });
    }

    @computedFrom(Reflection.getPropertyPath<WorkflowConfigurationViewModel>(x => x._selectedNode))
    public get selectedNode(): NetworkNode {
        return this._selectedNode;
    }

    set branchId(value: number) {
        this._branchId = value;
        this.getWorkflowConfigurations();
    }

    @computedFrom(Reflection.getPropertyPath<WorkflowConfigurationViewModel>(x => x._branchId))
    get branchId() {
        return this._branchId;
    }

    @computedFrom(Reflection.getPropertyPath<WorkflowConfigurationViewModel>(x => x._entityType))
    get entityTypeUri(): string {
        return AllEntityTypeConfigurationConstants.filter(x => x.entityType == this._entityType)[0].uri;
    }

    set entityType(value: number) {
        this._entityType = value;
        this.getWorkflowConfigurations();
    }

    @computedFrom(Reflection.getPropertyPath<WorkflowConfigurationViewModel>(x => x._entityType))
    get entityType(): number {
        return this._entityType;
    }

    @computedFrom(Reflection.getPropertyPath<WorkflowConfigurationViewModel>(x => x._selectedItem))
    get selectedItem(): WorkflowConfigurationModel {
        return this._selectedItem;
    }

    getWorkflowConfigurations() {
        this.selectedItem = null;
        if (this.entityType && this.branchId) {
            this.workflowConfigurationService.getWorkflowConfigurations(this.entityType, this.branchId).then(x => {
                this.workflowConfigurations = x.configurations;
                this.pathResults = x.pathResults;
                this.workflowActivitySignatures = x.workflowActivitySignatures;
                if (window["isDev"]) {
                    this._bindingSubscriptions.forEach(x => x.dispose());
                    this._bindingSubscriptions = [];
                    this.workflowActivitySignatures.forEach(z => {
                        if (!window[z.internalName]) {
                            window[z.internalName] = {};
                        }
                        const props = Object.keys(window[z.internalName]);
                        props.forEach(prop => {
                            this._bindingSubscriptions.push(this.bindingEngine.propertyObserver(window[z.internalName], prop).subscribe(() => {
                                this.selectedItem = this._selectedItem;
                            }));
                        });
                    });
                    if (!window['new-activity']) {
                        window['new-activity'] = {};
                    }
                    const props = Object.keys(window['new-activity']);
                    props.forEach(prop => {
                        this._bindingSubscriptions.push(this.bindingEngine.propertyObserver(window['new-activity'], prop).subscribe(() => {
                            this.selectedItem = this._selectedItem;
                        }));
                    });
                }

                if (this._params.id) {
                    this.selectConfiguration(this.workflowConfigurations.filter(z => z.workflowConfigurationId == this._params.id)[0]);
                    this._params.id = null;
                }
            });
        }
    }

    set selectedItem(value: WorkflowConfigurationModel) {

        if (value && !('name' in value)) {
            return;
        }

        //Unselect node if different configuration is selected
        if (!value || (this._selectedItem && value.workflowConfigurationId != this._selectedItem.workflowConfigurationId)) {
            this.selectedNode = null;
        }

        this._selectedItem = value;

        // destroy previous network
        if (this._network) {
            this._network.off('selectNode', this.onNodeSelected);
            this._network.off('deselectNode', this.onNodeDeselected);
            this._network.destroy();
            this._network = null;
        }

        // destroy previous unit of work
        if (this.uow) {
            this.uow.dispose();
            this.uow = null;
        }
        if (value) {
            setTimeout(() => {
                this._networkStructure = this.buildNetworkStructure();
                this._networkStructure.subscribeOnNodeAdded(this.onNewNodeAdded);
                this._networkStructure.subscribeOnNodesRemoved(this.onNodesRemoved);
                this._networkStructure.subscribeOnLinkAdded(this.onNewLinkAdded);
                this.uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.selectedItem, []);
                if (!this.selectedItem.workflowConfigurationId) {
                    this.uow.addChange(x => x.entityTypeId, this._entityType, 0);
                    this.uow.addChange(x => x.isActive, true, false);
                    this.uow.addChange(x => x.isSelectable, true, false);
                    this.uow.addChange(x => x.guid, Guid.generateGuid(), null);
                    this.uow.addChange(x => x.branchId, this.branchId, null);
                }
                setTimeout(() => {
                    this.createNetwork();
                }, 100);
            });
        }
    }

    activate(params: { id: number, entityTypeId: number }) {
        this._params = params;

        this.workflowConfigurationService.getEntityTypesForWorkflow().then(x => {
            this.entityTypes = x.entityTypes;
            this.branchId = this.userClientService.currentUser.branchId;
            setTimeout(() => {
                if (this._params && this._params.entityTypeId) {
                    this.entityType = Number(this._params.entityTypeId);
                } else {
                    this.entityType = this.entityTypes[0].entityTypeId;
                }
            });
        });
        this.canCreateWorkflowConfiguration = this.userClaimsService.hasPermission(BaseClaims.CreateWorkflowConfiguration);
    }

    buildHierarchy(arry: WorkflowActivityModel[]) {

        const roots = [], children = {};

        // find the top level nodes and hash the children based on parent
        for (let i = 0, len = arry.length; i < len; ++i) {
            const item = arry[i],
                p = item.parentActivityGuid,
                target = !p ? roots : (children[p] || (children[p] = []));

            target.push({ value: item });
        }

        const level = 0;
        // function to recursively build the tree
        const findChildren = (parent, level) => {
            parent.level = level;
            if (children[parent.value.guid]) {
                parent.children = children[parent.value.guid] ? children[parent.value.guid] : [];
                for (let i = 0, len = parent.children.length; i < len; ++i) {
                    findChildren(parent.children[i], level + 1);
                }
            }
        };

        // enumerate through to handle the case where there are multiple roots
        for (let i = 0, len = roots.length; i < len; ++i) {
            findChildren(roots[i], level);
        }

        return roots[0];
    }

    buildNetworkStructure(): NetworkStructure {
        const config = this._selectedItem;
        const networkStructure = new NetworkStructure();
        const activities = Enumerable.from(config.workflowActivities).orderBy(x => x.workflowActivityId).toArray();
        activities.forEach(activity => {
            this.createNetworkLink(networkStructure, activity.parentActivityGuid, activity.guid, activity.pathResult);
        });
        const tree = this.buildHierarchy(activities);
        if (tree) {
            this.processTree(networkStructure, tree);
        } else {
            this.attachAddNewActivityNode(networkStructure, null, null);
        }
        return networkStructure;
    }

    @computedFrom('selectedNode.data.activity.internalName')
    public get getViewModel() {
        if (this.selectedNode.data.activity.internalName) {
            require('views/plugins/workflow-activities/' + this.selectedNode.data.activity.internalName + '/activity-configuration.ts')
            require('views/plugins/workflow-activities/' + this.selectedNode.data.activity.internalName + '/activity-configuration.html')
            return `views/plugins/workflow-activities/${this.selectedNode.data.activity.internalName}/activity-configuration`
        }
    }

    processTree(networkStructure, tree: any) {
        const activity = tree.value as WorkflowActivityModel;
        const level = tree.level;
        const childNode = new NetworkNode();
        const signature = this.workflowActivitySignatures.filter(x => x.internalName == activity.internalName)[0];
        childNode.id = activity.guid;
        childNode.data = { activity };
        childNode.level = level;
        childNode.text = activity.name;
        childNode.shape = signature.shape;
        childNode.color = signature.background;
        childNode.fontColor = "black";
        networkStructure.addNode(childNode);
        if (!tree.children) {
            tree.children = [];
        }
        if (signature.pathResults && signature.pathResults.length > 0) {

            const activities: WorkflowActivityModel[] = [];
            tree.children.forEach(child => {
                const activity = child.value as WorkflowActivityModel;
                activities.push(activity);
                this.processTree(networkStructure, child);
            });

            signature.pathResults.filter(x => activities.every(y => y.pathResult != x)).forEach(path => {
                this.attachAddNewActivityNode(networkStructure, childNode, path);
            });
        } else {
            if (!tree.children || tree.children.length == 0) {
                this.attachAddNewActivityNode(networkStructure, childNode, null);
            } else {
                tree.children.forEach(child => {
                    this.processTree(networkStructure, child);
                });
            }
        }
    }

    constructor(private workflowConfigurationService: WorkflowConfigurationService,
        private notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService,
        private userClientService: UserClientService,
        private userClaimsService: UserClaimsService,
        private validationsClientService: ValidationsClientService,
        private bindingEngine: BindingEngine,
        private i18N: I18N) {
    }

    onActivityNameChanged() {
        const node = this._nodes.get(this.selectedNode.id);
        node.label = this.selectedNode.text = this.selectedNode.data.activity.name;
        this._nodes.update(node);
    }

    onNewLinkAdded = (link: NetworkLink) => {
        this._edges.add([this.getVisEdge(link)]);
    }

    onNodesRemoved = (nodes: NetworkNode[]) => {
        this._nodes.remove(nodes.map(node => node.id));
    }

    onNewNodeAdded = (node: NetworkNode) => {
        this._nodes.add([this.getVisNode(node)]);
    }

    onNewActivitySelected(activitySignature: WorkflowActivitySignature) {
        if (!this._network) {
            this.createNetwork();
        }
        const internalName = activitySignature.internalName;
        const activity = this.addNewActivity(internalName, this.selectedNode.id);
        this.selectedNode.applyDefaults();
        this.selectedNode.id = activity.guid;
        this.selectedNode.data = { activity };
        this.selectedNode.text = activity.name = "Activity" + "#" + this.selectedItem.workflowActivities.length;
        this.selectedNode.color = activitySignature.background;
        this.selectedNode.fontColor = "white";
        this.selectedNode.shape = activitySignature.shape;
        const sourceNode = this._networkStructure.getSourceNodeFromTargetNode(this.selectedNode.id);
        if (sourceNode) {
            const linkForPath = this._networkStructure.getLink(sourceNode.id, this.selectedNode.id)
            //this._networkStructure.removeNode(this.selectedNode.id, true);
            const sourceActivity = this.selectedItem.workflowActivities.filter(z => z.guid == sourceNode.id)[0];
            if (sourceActivity) {
                const sourceActivitySignature = this.workflowActivitySignatures.filter(x => x.internalName == sourceActivity.internalName)[0];
                activity.pathResult = sourceActivitySignature.pathResults.filter(x => x == linkForPath.data.path)[0];
                activity.parentActivityGuid = sourceActivity.guid;
            }
            const linkText = activity.pathResult ? this.pathResults.filter(x => x.workflowActivityPathResultId == activity.pathResult)[0].name : "";
            linkForPath.text = linkText;
            linkForPath.data = { path: activity.pathResult };
            //this.createNetworkLink(this._networkStructure, sourceNode.id, this.selectedNode.id, activity.pathResult);
        }
        if (activitySignature.pathResults && activitySignature.pathResults.length > 0) {
            activitySignature.pathResults.forEach(path => {
                this.attachAddNewActivityNode(this._networkStructure, this.selectedNode, path);
            });
        } else {
            this.attachAddNewActivityNode(this._networkStructure, this.selectedNode, null);
        }
        this._nodes.update(this.getVisNode(this.selectedNode));
    }

    attachAddNewActivityNode(networkStructure: NetworkStructure, node: NetworkNode, pathNumber: number) {
        const newActivityNode = this.createNewActivityNode(Guid.generateGuid(), node ? (node.level + 1) : 0);
        networkStructure.addNode(newActivityNode);
        if (node) {
            this.createNetworkLink(networkStructure, node.id, newActivityNode.id, pathNumber);
        }
    }

    private createNetworkLink(networkStructure: NetworkStructure, sourceNodeId: string, targetNodeId: string, pathNumber: number) {
        const linkText = pathNumber ? this.pathResults.filter(x => x.workflowActivityPathResultId == pathNumber)[0].name : "";
        const link = new NetworkLink(sourceNodeId, targetNodeId, linkText, { path: pathNumber });
        networkStructure.addLink(link);
    }

    onNodeSelected = (values: { nodes: string[] }) => {
        const nodeSelectedId = values.nodes[0];

        if (this.selectedNode && this.selectedNode.id != nodeSelectedId) {
            if (this.activityConfigComposer && this.activityConfigComposer["currentViewModel"]) {
                const configurationVM = this.activityConfigComposer["currentViewModel"] as BaseActivityConfiguration;
                configurationVM.setMeta().then(() => {
                    this.selectedNode = null;
                });
            } else {
                this.selectedNode = null;
            }
        }

        if (nodeSelectedId && (!this.selectedNode || this.selectedNode.id != nodeSelectedId)) {
            if (this.activityConfigComposer && this.activityConfigComposer["currentViewModel"]) {
                const configurationVM = this.activityConfigComposer["currentViewModel"] as BaseActivityConfiguration;
                configurationVM.setMeta().then(() => {
                    this.selectedNode = this._networkStructure.findNodeById(nodeSelectedId);
                });
            } else {
                this.selectedNode = this._networkStructure.findNodeById(nodeSelectedId);
            }
        }
    }

    onNodeDeselected = () => {
        if (this.activityConfigComposer && this.activityConfigComposer["currentViewModel"]) {
            const configurationVM = this.activityConfigComposer["currentViewModel"] as BaseActivityConfiguration;
            configurationVM.setMeta().then(() => {
                this.selectedNode = null;
            });
        } else {
            this.selectedNode = null;
        }
    }

    private createNewActivityNode(id: string, level: number): NetworkNode {
        const childNode = new NetworkNode();
        childNode.id = id;
        childNode.data = { isNewActivity: true };
        childNode.level = level;
        childNode.text = "+";
        childNode.size = { height: 50, width: 50 };
        childNode.fontSize = 25;
        childNode.color = "orange";
        childNode.fontColor = "white";
        childNode.shape = "box";
        return childNode;
    }

    createNetwork() {
        if (BrowserHelper.isIE()) {
            return;
        }

        this._nodes = new vis.DataSet();
        // create an array with edges
        this._edges = new vis.DataSet();

        this.linkNetworkData();

        // provide the data in the vis format
        const data = {
            nodes: this._nodes,
            edges: this._edges
        };

        const options = {
            layout:
            {
                hierarchical:
                {
                    enabled: true,
                    direction: 'UD',
                    nodeSpacing: 250
                }
            },
            physics: {
                enabled: false
            },
            width: this.divNetwork.offsetWidth + "px",
            height: this.divNetwork.offsetHeight + "px",
            interaction: {
                hover: true,
                dragNodes: false,
                hoverConnectedEdges: false
            }
        };

        this._network = new vis.Network(this.divNetwork, data, options);

        this._network.on('selectNode', this.onNodeSelected);
        this._network.on('deselectNode', this.onNodeDeselected);
        this._network.on('hoverNode', this.changeCursorToPointer);
        this._network.on('blurNode', this.changeCursorToOpenHand);
        this._network.on('dragging', this.changeCursorToDragging);
        this._network.on('dragStart', this.changeCursorToDragging);
        this._network.on('dragEnd', this.changeCursorToOpenHand);

        if (this.selectedNode) {
            this._network.selectNodes([this.selectedNode.id]);
        }
        setTimeout(() => {
            if (!this.networkCanvas) {
                this.networkCanvas = this.divNetwork.getElementsByTagName("canvas")[0];
            }
            this.networkCanvas.style.cursor = '-webkit-grab';
        });
    }

    getActivityType(internalName: string) {
        return this.workflowActivitySignatures.filter(x => x.internalName == internalName)[0].displayName;
    }

    changeCursorToPointer = () => {
        this.networkCanvas.style.cursor = 'pointer';
    }

    changeCursorToOpenHand = () => {
        this.networkCanvas.style.cursor = '-webkit-grab';
    }

    changeCursorToDragging = () => {
        this.networkCanvas.style.cursor = '-webkit-grabbing';
    }

    linkNetworkData() {

        this._networkStructure.getNodes().forEach(node => {
            this._nodes._addItem(this.getVisNode(node));
        });

        this._networkStructure.getLinks().forEach(link => {
            this._edges._addItem(this.getVisEdge(link));
        });
    }

    showCloneDialog() {
        if (!this.canCreateWorkflowConfiguration) return;

        const model = {
            cloneFunction: (name: string) => this.clone(this.selectedItem, name)
        }
        this.dialogClientService.show(DialogServiceConstants.URLs.CloneWorkflowDialog, model);
    }

    clone(selectedItem: WorkflowConfigurationModel, name: string) {
        return new Promise<void>((resolve, reject) => {
            const request: CloneWorkflowConfigurationRequest = {
                workflowConfigurationId: selectedItem.workflowConfigurationId,
                clonedWorkflowName: name
            };

            this.workflowConfigurationService.cloneWorkflowConfiguration(request)
                .then(x => {
                    if (x.isSuccess) {
                        this.notificationClientService.show(this.i18N.tr('successful-cloning'), this.i18N.tr('successful-workflow-cloning-description'), NotificationType.Success);
                        this.workflowConfigurations.push(x.workflowConfiguration);
                        resolve();
                    }
                    else {
                        this.notificationClientService.show(this.i18N.tr('failed-cloning'), this.i18N.tr('failed-workflow-cloning-description'), NotificationType.Success);
                        reject();
                    }
                })
                .catch(() => {
                    this.notificationClientService.show(this.i18N.tr('error-text'), this.i18N.tr('error-workflow-cloning-description'), NotificationType.Success);
                    reject();
                });
        });
    }

    save() {
        return new Promise<void>((resolve, reject) => {
            const innerSaveFunc = () => {
                this.workflowConfigurationService.saveWorkflowConfiguration({ id: this.selectedItem.workflowConfigurationId, changes: this.uow.getChanges() }).then(response => {
                    if (response.validationResult && response.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(response.validationResult);
                    } else if (response.workflowConfiguration) {
                        if (!this.selectedItem.workflowConfigurationId) {
                            this.workflowConfigurations.push(response.workflowConfiguration);
                        } else {
                            const index = ArrayUtils.findIndex(this.workflowConfigurations, y => y.workflowConfigurationId == response.workflowConfiguration.workflowConfigurationId);
                            if (index > -1) {
                                this.workflowConfigurations.splice(index, 1, response.workflowConfiguration);
                            }
                        }
                        this.selectedItem = response.workflowConfiguration;
                        this.notificationClientService.show("Saved", "Saved successfully", NotificationType.Success);
                    } else {
                        this.notificationClientService.show("An error occured while saving", "", NotificationType.Error);
                    }
                    this.getWorkflowConfigurations();
                    resolve();
                }).catch(reject);
            }
            if (this.activityConfigComposer && this.activityConfigComposer["currentViewModel"]) {
                const configurationVM = this.activityConfigComposer["currentViewModel"] as BaseActivityConfiguration;
                configurationVM.setMeta().then(() => {
                    innerSaveFunc();
                });
            } else {
                innerSaveFunc();
            }
        });
    }

    removeConfig(config: WorkflowConfigurationModel) {
        event.stopPropagation();
        this.dialogClientService.show(DialogServiceConstants.URLs.DeleteModal, { delete: () => this.delete(config.workflowConfigurationId) });
    }

    delete = (workflowConfigurationId: number) => {
        return new Promise<void>((resolve, reject) => {
            this.workflowConfigurationService.deleteWorkflowConfiguration(workflowConfigurationId).then(() => {
                const index = ArrayUtils.findIndex(this.workflowConfigurations, x => x.workflowConfigurationId == workflowConfigurationId);
                if (index > -1) {
                    this.workflowConfigurations.splice(index, 1);
                }
                this.notificationClientService.show("Deleted", "Configuration deleted successfully", NotificationType.Success);
                this.dialogClientService.hide();
                resolve();
            }).catch(() => {
                this.notificationClientService.show("Deleted", "Failed to delete configuration", NotificationType.Error);
                this.dialogClientService.hide();
                reject();
            });
        });
    }

    cancel() {
        const index = this.workflowConfigurations.findIndex(x => x == this.selectedItem);
        if (index > -1) {
            if (this._originalSelectedItem && this._originalSelectedItem.workflowConfigurationId > 0) {
                const clone = CloneHelper.cloneObject(this._originalSelectedItem);
                this.workflowConfigurations.splice(index, 1, clone);
                this.selectedItem = clone;
                this.selectedNode = null;
            } else {
                this.workflowConfigurations.splice(index, 1);
                this.selectedItem = null;
            }
        }
        if (this.uow) {
            this.uow.dispose();
            this.uow = null;
        }
    }

    createNewWorkflowConfiguration() {
        if (!this.canCreateWorkflowConfiguration) return;

        this.selectedItem = {
            entityTypeId: 0,
            guid: null,
            isActive: false,
            isSelectable: false,
            name: '',
            workflowConfigurationId: 0,
            workflowActivities: [],
            branchId: this.branchId,
            entityType: EntityTypes.WorkflowConfiguration
        }
    }

    deleteSelectedNode() {
        const sourceNode = this._networkStructure.getSourceNodeFromTargetNode(this.selectedNode.id);
        const path = this.selectedNode.data.activity.pathResult;
        const nodesRemoved = this._networkStructure.removeNode(this.selectedNode.id);
        nodesRemoved.forEach(nodeRemoved => {
            const index = ArrayUtils.findIndex(this.selectedItem.workflowActivities, x => nodeRemoved.data.activity && x.guid == nodeRemoved.data.activity.guid);
            if (index > -1) {
                this.selectedItem.workflowActivities.splice(index, 1);
                this.selectedNode = null;
            }
        });
        this.attachAddNewActivityNode(this._networkStructure, sourceNode, path);
    }

    selectConfiguration(config: WorkflowConfigurationModel) {
        this._originalSelectedItem = CloneHelper.cloneObject(config);
        this.selectedItem = config;
    }

    addNewActivity(internalName: string, id: string): WorkflowActivityModel {
        const activity: WorkflowActivityModel = {
            actionTakerDepartmentId: null,
            creationDate: new Date(),
            isActive: true,
            guid: id,
            isSelectable: true,
            lastModified: new Date(),
            meta: "",
            name: '',
            internalName: internalName,
            workflowConfigurationId: this.selectedItem.workflowConfigurationId,
            workflowActivityId: 0,
            parentActivityGuid: null,
            includeSubordinates: false
        };
        this.selectedItem.workflowActivities.push(activity);
        return activity;
    }

    removeActivity(step: WorkflowActivityModel) {
        const index = ArrayUtils.findIndex(this.selectedItem.workflowActivities, x => x == step);
        if (index > -1) {
            this.selectedItem.workflowActivities.splice(index, 1);
        }
    }

    detached() {
        if (this.uow) {
            this.uow.dispose();
        }
        this._bindingSubscriptions.forEach(z => z.dispose());
        this._bindingSubscriptions = [];
    }

    private getVisNode(node: NetworkNode) {

        const activity = node.data.activity as WorkflowActivityModel;
        const internalName = activity ? activity.internalName : 'new-activity';

        if (internalName && !window[internalName]) {
            window[internalName] = {}
        }
        return {
            id: node.id,
            label: node.text,
            shape: (internalName && window[internalName].shape) || node.shape,
            level: node.level,
            color: (internalName && window[internalName].color) || node.color,
            font: {
                color: (internalName && window[internalName].fontColor) || node.fontColor,
                size: (internalName && window[internalName].fontSize) || node.fontSize,
                face: (internalName && window[internalName].fontFace) || node.fontFace,
                background: (internalName && window[internalName].fontBackground) || node.fontBackground
            },
            widthConstraint: {
                minimum: node.size ? node.size.width : 100,
                maximum: node.size ? node.size.width : 100
            },
            heightConstraint: {
                minimum: node.size ? node.size.height : 100,
                maximum: node.size ? node.size.height : 100
            },
            shadow: true,
            size: 50,
            image: undefined
        }
    }

    private getVisEdge(link: NetworkLink) {
        return {
            from: link.sourceId,
            to: link.targetId,
            arrows: {
                to: { enabled: true, scaleFactor: 0.5, type: 'arrow' },
                middle: { enabled: false, scaleFactor: 0.5, type: 'arrow' },
                from: { enabled: false, scaleFactor: 0.5, type: 'arrow' }
            },
            arrowStrikethrough: true,
            dashes: false,
            label: link.text,
            color: {
                color: 'grey',
                highlight: 'grey',
                hover: 'grey',
                inherit: 'from',
                opacity: 1.0
            },
            selectionWidth: (width) => { return width },
            labelHighlightBold: false,
            font: {
                color: '#343434',
                size: 15, // px
                face: 'arial',
                background: '#ffffff',
                strokeWidth: 2, // px
                strokeColor: '#ffffff',
                align: 'horizontal',
                multi: false,
                vadjust: 0
            }
        }
    }

}
